var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Icon from '../../../atoms/Icon';
import { useListNavigation } from '../../../../hooks/useListNavigation';
import { useClickOutside } from '../../../../hooks/useClickOutside';
import * as styles from './select.linaria';
import InputField from '../InputField';
function Select(_a) {
    var { label, hideLabel, id, className, labelClassName, selectClassName, contentClassName, defaultValue, topOptions, options, topNLabel, optionsLabel, placeholder, onChange, disabled, readOnly, trimSelection } = _a, args = __rest(_a, ["label", "hideLabel", "id", "className", "labelClassName", "selectClassName", "contentClassName", "defaultValue", "topOptions", "options", "topNLabel", "optionsLabel", "placeholder", "onChange", "disabled", "readOnly", "trimSelection"]);
    const allOptions = [...topOptions, ...options];
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);
    const [isOptionsOpen, setIsOptionsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState(defaultValue || (placeholder ? null : allOptions[0]));
    useEffect(() => {
        if (defaultValue) {
            setSelectedOption(defaultValue);
        }
    }, [defaultValue]);
    const onValueChange = (val, closeSelect) => {
        if (closeSelect)
            setIsOptionsOpen(false);
        setSelectedOption(val);
        const newOption = allOptions.find((option) => option === val);
        onChange(newOption);
    };
    const onSelect = (index, closeSelect = true) => {
        onValueChange(allOptions[index], closeSelect);
        if (closeSelect) {
            setIsOptionsOpen(false);
        }
    };
    const onCancel = () => {
        setIsOptionsOpen(false);
    };
    /**
     * useListNavigation hook is currently handling keyboard navigation through the list
     */
    const { selectedItemIndex, handleKeyDown, handleCancel } = useListNavigation(onCancel, onSelect, allOptions.length, true);
    useClickOutside(inputRef, handleCancel, null);
    const toggleOptions = (isOpen) => {
        if (!disabled) {
            setIsOptionsOpen(!isOpen);
        }
    };
    const onInputKeyDown = (e) => {
        if (!disabled) {
            setIsOptionsOpen(true);
            handleKeyDown(e);
        }
    };
    const renderOptions = (optionElements, optionsId) => optionElements.map((option) => (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <li key={`item_${optionsId}_${id}_${option.value}`} id={`item_${optionsId}_${id}_${option.value}`} role="option" aria-selected={(selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value) === option.value} tabIndex={0} className={classNames(selectedItemIndex !== undefined &&
            allOptions[selectedItemIndex].value === option.value &&
            'focused')} onClick={() => {
            onValueChange(option, true);
        }}>
        {option.label}
      </li>));
    /**
     * We are emulating normal select behaviour with upwards and downwards navigation
     * with keyboard (arrow up/down)
     *
     * We estimate the height of the items, could change a bit if there are labels,
     * but shouldn't be much of a problem.
     */
    useEffect(() => {
        const dropRef = dropdownRef.current;
        if (selectedItemIndex !== undefined && dropRef) {
            const itemHeight = dropRef.scrollHeight / allOptions.length;
            let top = 0;
            if (selectedItemIndex !== 0) {
                top = selectedItemIndex * itemHeight - 2 * itemHeight;
            }
            dropRef.scroll({
                top,
                left: 0,
                behavior: 'instant',
            });
        }
    }, [selectedItemIndex, dropdownRef.current]);
    /**
     * It’s important to use semantic elements like input and ul to tell an assistive technology like a screen reader
     * that the element will do something when clicked (input) or that it is a list of things in a group (ul).
     *
     * The tab-index=-1 on the unordered list will force a focus on it once the dropdown is open,
     * while the tab-index=0 on the list items tells the device that these items can be tabbed through.
     *
     * An attribute called aria-haspopup is to indicate that it has a popup when clicked,
     * And set listbox as its value to indicate what kind of popup this is.
     * An attribute aria-expanded to indicate whether our custom select element is expanded or collapsed.
     *
     * aria-activedescendant on our list will also allow us to specify which option is currently selected.
     *
     * Each of our options should have a role of option to indicate that they're an option,
     * an aria-selected to tell us whether this option is currently selected or not,
     * and an ID which our aria-activedescendant can reference.
     */
    return (<div className={classNames([
            styles.Select,
            className,
            disabled && 'disabled',
            args.errorMessage && 'error',
        ])} ref={inputRef}>
      <InputField {...args} id={id} label={label} hideLabel={hideLabel} labelClassName={labelClassName} inputClassName={selectClassName} value={selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label} placeholder={placeholder} disabled={disabled} readOnly={readOnly} rightContent={<Icon icon="Down"/>} onClick={() => toggleOptions(isOptionsOpen)} onKeyDown={onInputKeyDown} ariaHasPopup="listbox" ariaExpanded={isOptionsOpen}/>
      <ul className={`selectList ${isOptionsOpen ? '' : 'hide'}`} role="listbox" aria-activedescendant={selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value} tabIndex={-1} ref={dropdownRef}>
        {/* Top Frequent Options */}
        {topOptions.length > 0 && (<>
            {topNLabel && <span className="selectGroupLabel">{topNLabel}</span>}
            {renderOptions(topOptions, 'top')}
            <div className="selectSeparator"/>
          </>)}
        {optionsLabel && (<span className="selectGroupLabel">{optionsLabel}</span>)}
        {renderOptions(options, 'regular')}
      </ul>
    </div>);
}
Select.defaultProps = {
    className: null,
    selectClassName: null,
    contentClassName: null,
    defaultValue: null,
    topNLabel: null,
    trimSelection: false,
    optionsLabel: 'optionsLabel',
    labelClassName: null,
    hideLabel: false,
    topOptions: [],
    options: [],
    onChange: undefined,
    disabled: false,
    readOnly: true,
};
export default Select;
